// src/app/pages/admin/ProjectManagementPage.jsx
import { useEffect, useState } from "react";
import { admin } from "../../services/adminApi";
import {
  Search, FolderOpen, Eye, CheckCircle, XCircle, Clock,
  Calendar, User, RefreshCw, ChevronDown,
} from "lucide-react";

const T = {
  fontDisplay: "'DM Sans', sans-serif",
  fontBody:    "'DM Sans', sans-serif",
  fontMono: "'DM Sans', sans-serif",
  amber:       "#111111",
  amberDark:   "#333333",
  amberBg:     "rgba(0,0,0,0.06)",
  amberBorder: "rgba(0,0,0,0.12)",
  dark:        "#0c1118",
  bg:          "#f4f6f8",
  surface:     "#ffffff",
  border:      "#e8ecf0",
  muted:       "#6b7280",
  mutedLight:  "#9ca3af",
  green:       "#16a34a",
  greenBg:     "rgba(34,197,94,0.1)",
  greenBorder: "rgba(34,197,94,0.25)",
  red:         "#dc2626",
  redBg:       "rgba(239,68,68,0.1)",
  redBorder:   "rgba(239,68,68,0.25)",
};

/* ── inject styles once ─────────────────────────────────────────── */
if (!document.getElementById("apm-styles")) {
  const s = document.createElement("style");
  s.id = "apm-styles";
  s.textContent = `
    @keyframes apm-spin { to { transform: rotate(360deg); } }
    .apm-spin { animation: apm-spin 0.7s linear infinite; }
    .apm-row:hover td { background: rgba(0,0,0,0.03); }
    .apm-sort-btn:hover { color: #0c1118; }
    .apm-btn:hover { opacity: 0.8; }
    .apm-search:focus { outline: none; border-color: #111111; box-shadow: 0 0 0 2px rgba(0,0,0,0.12); }

    /* Page wrapper */
    .apm-page {
      min-height: 100vh;
      background: #f4f6f8;
      font-family: 'DM Sans', sans-serif;
      padding: 0;
      box-sizing: border-box;
      width: 100%;
      overflow-x: hidden;
    }

    /* KPI grid */
    .apm-kpi-grid {
      display: grid;
      grid-template-columns: repeat(4, 1fr);
      gap: 14px;
      margin-bottom: 20px;
    }

    /* Table wrap */
    .apm-table-wrap {
      background: #fff;
      border: 1px solid #e8ecf0;
      border-radius: 10px;
      overflow: hidden;
      margin-bottom: 20px;
      position: relative;
      width: 100%;
      box-sizing: border-box;
    }

    /* Pagination */
    .apm-pager {
      background: #fff;
      border: 1px solid #e8ecf0;
      border-radius: 10px;
      padding: 14px 20px;
      display: flex;
      align-items: center;
      justify-content: space-between;
      flex-wrap: wrap;
      gap: 12px;
      box-sizing: border-box;
    }
    .apm-pager-pages {
      display: flex;
      align-items: center;
      gap: 8px;
      flex-wrap: wrap;
    }

    /* Card (mobile) */
    .apm-card {
      background: #fff;
      border: 1px solid #e8ecf0;
      border-radius: 12px;
      overflow: hidden;
    }

    /* ── Responsive ──────────────────────────────────────────── */
    @media (max-width: 1100px) {
      .apm-kpi-grid { grid-template-columns: repeat(2, 1fr); }
    }
    @media (max-width: 640px) {
      .apm-kpi-grid { grid-template-columns: repeat(2, 1fr); gap: 10px; }
      .apm-pager { flex-direction: column; align-items: stretch; }
      .apm-pager-pages { justify-content: center; }
    }
    @media (max-width: 400px) {
      .apm-kpi-grid { grid-template-columns: 1fr; }
    }
  `;
  document.head.appendChild(s);
}

/* ── helpers ────────────────────────────────────────────────────── */
function useIsMobile(bp = 768) {
  const get = () => typeof window !== "undefined" && window.innerWidth <= bp;
  const [v, setV] = useState(get);
  useEffect(() => {
    const fn = () => setV(get());
    window.addEventListener("resize", fn);
    return () => window.removeEventListener("resize", fn);
  }, [bp]); // eslint-disable-line
  return v;
}

function statusStyle(s) {
  if (s === "approved") return { bg: T.greenBg, color: T.green, border: T.greenBorder };
  if (s === "rejected") return { bg: T.redBg, color: T.red, border: T.redBorder };
  return { bg: T.amberBg, color: T.amberDark, border: T.amberBorder };
}
function formatDate(d) {
  const days = Math.floor((Date.now() - new Date(d)) / 86400000);
  if (days === 0) return "Today";
  if (days === 1) return "Yesterday";
  if (days < 7)  return `${days} days ago`;
  return new Date(d).toLocaleDateString();
}

/* ── StatusBadge / PriorityBadge ────────────────────────────────── */
function StatusBadge({ status }) {
  const ss = statusStyle(status);
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 4, padding: "3px 10px", borderRadius: 99, fontSize: 11, fontWeight: 600, fontFamily: T.fontBody, background: ss.bg, color: ss.color, border: `1px solid ${ss.border}` }}>
      {status === "active" && <CheckCircle size={11} />}
      {status === "archived" && <XCircle size={11} />}
      {/* {status === "pending"  && <Clock size={11} />} */}
      {status}
    </span>
  );
}
/* ═══════════════════════════════════════════════════════════════════
   PAGE
═══════════════════════════════════════════════════════════════════ */
export default function ProjectManagementPage() {
  const isMobile = useIsMobile();

  const [q, setQ]                     = useState("");
  const [status, setStatus]           = useState("all");
  const [page, setPage]               = useState(1);
  const [rows, setRows]               = useState([]);
  const [total, setTotal]             = useState(0);
  const [loading, setLoading]         = useState(false);
  const [sortField, setSortField]     = useState("createdAt");
  const [sortDir, setSortDir]         = useState("desc");

  async function load() {
    setLoading(true);
    try {
      const r = await admin.submissions({ q, status, page, pageSize: 10, sortField, sortDirection: sortDir });
      setRows(r.items || []);
      setTotal(r.total || 0);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }

  useEffect(() => { load(); }, [q, status, page, sortField, sortDir]); // eslint-disable-line

  const stats = {
    total,
    pending:  rows.filter((p) => p.status === "pending").length,
    approved: rows.filter((p) => p.status === "approved").length,
    rejected: rows.filter((p) => p.status === "rejected").length,
  };

  const kpis = [
    { label: "Total Projects", value: stats.total,    accent: T.amber,  Icon: FolderOpen },
    { label: "Pending Review", value: stats.pending,  accent: "#111111", Icon: Clock },
    { label: "Approved",       value: stats.approved, accent: T.green,  Icon: CheckCircle },
    { label: "Rejected",       value: stats.rejected, accent: T.red,    Icon: XCircle },
  ];

  const pages    = Math.max(1, Math.ceil(total / 10));
  const startItem = (page - 1) * 10 + 1;
  const endItem   = Math.min(page * 10, total);

  return (
    <div className="apm-page">

      {/* ── Header ──────────────────────────────────────────────── */}
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", flexWrap: "wrap", gap: 12, marginBottom: 20 }}>
        <div>
          <h1 style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 24, fontWeight: 700, color: "#0c1118", margin: 0, lineHeight: 1.2 }}>Project Management</h1>
          <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 13, color: "#6b7280", margin: "4px 0 0" }}>Review and manage project submissions</p>
        </div>
        <button
          className="apm-btn"
          onClick={load}
          disabled={loading}
          style={{ display: "flex", alignItems: "center", gap: 7, padding: "9px 18px", background: loading ? "#f3f4f6" : T.dark, color: loading ? T.mutedLight : "#fff", border: "none", borderRadius: 9, fontFamily: T.fontBody, fontSize: ".82rem", fontWeight: 600, cursor: loading ? "not-allowed" : "pointer" }}
        >
          <RefreshCw size={13} className={loading ? "apm-spin" : ""} />
          {!isMobile && "Refresh"}
        </button>
      </div>

      {/* ── KPI Cards ───────────────────────────────────────────── */}
      <div className="apm-kpi-grid">
        {kpis.map((k) => (
          <div key={k.label} style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 12, padding: 18, borderLeft: `4px solid ${k.accent}`, boxSizing: "border-box" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
              <p style={{ fontFamily: T.fontBody, fontSize: 10, fontWeight: 700, letterSpacing: "0.08em", color: T.muted, margin: 0 }}>{k.label}</p>
              <k.Icon size={16} color={k.accent} />
            </div>
            <p style={{ fontFamily: T.fontBody, fontSize: 32, fontWeight: 700, color: T.dark, margin: 0, lineHeight: 1 }}>{k.value}</p>
          </div>
        ))}
      </div>

      {/* ── Search + Status filter ───────────────────────────────── */}
      <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 10, padding: 16, marginBottom: 16, boxSizing: "border-box", width: "100%" }}>
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
          {/* Search */}
          <div style={{ flex: 1, minWidth: 0, position: "relative" }}>
            <Search size={15} style={{ position: "absolute", left: 11, top: "50%", transform: "translateY(-50%)", color: T.mutedLight, pointerEvents: "none" }} />
            <input
              className="apm-search"
              style={{ width: "100%", padding: "10px 12px 10px 34px", border: `1px solid ${T.border}`, borderRadius: 8, fontSize: 13, fontFamily: T.fontBody, background: T.surface, color: T.dark, boxSizing: "border-box" }}
              placeholder="Search by project name, user, or ID…"
              value={q}
              onChange={(e) => { setQ(e.target.value); setPage(1); }}
            />
          </div>
          {/* Status filter */}
          <div style={{ position: "relative", flexShrink: 0 }}>
            <select
              value={status}
              onChange={(e) => { setStatus(e.target.value); setPage(1); }}
              style={{ padding: "10px 32px 10px 12px", border: `1px solid ${T.border}`, borderRadius: 8, fontSize: 13, fontFamily: T.fontBody, background: T.surface, color: T.dark, appearance: "none", cursor: "pointer", boxSizing: "border-box" }}
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="archived">Archieved</option>
            </select>
            <ChevronDown size={13} style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", color: T.mutedLight, pointerEvents: "none" }} />
          </div>
        </div>
      </div>

      {/* ── Table (desktop) / Cards (mobile) ────────────────────── */}
      {isMobile ? (
        /* Mobile card list */
        <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 16, position: "relative" }}>
          {loading && (
            <div style={{ position: "absolute", inset: 0, background: "rgba(244,246,248,.85)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 2, borderRadius: 12 }}>
              <RefreshCw size={20} className="apm-spin" color={T.amber} />
            </div>
          )}
          {rows.length === 0 && !loading ? (
            <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 12, display: "flex", flexDirection: "column", alignItems: "center", padding: "48px 24px", gap: 10 }}>
              <FolderOpen size={36} color={T.border} />
              <p style={{ color: T.muted, margin: 0, fontWeight: 600 }}>No projects found</p>
              <p style={{ color: T.mutedLight, fontSize: 12, margin: 0 }}>Try adjusting your search criteria</p>
            </div>
          ) : (
            rows.map((project) => (
              <ProjectCard key={project.id} project={project} onUpdate={load} />
            ))
          )}
        </div>
      ) : (
        /* Desktop table */
        <div className="apm-table-wrap">
          {loading && (
            <div style={{ position: "absolute", inset: 0, background: "rgba(255,255,255,0.8)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 2 }}>
              <RefreshCw size={20} className="apm-spin" color={T.muted} />
            </div>
          )}
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
              <thead>
                <tr style={{ background: T.bg, borderBottom: `1px solid ${T.border}` }}>
                  {[{ label: "Project", field: "name" }, { label: "Submitted By", field: "ownerEmail" }, { label: "Date Submitted", field: "createdAt" }].map(({ label, field }) => {
                    const isActive = sortField === field;
                    return (
                      <th key={field} style={{ padding: "12px 20px", textAlign: "left" }}>
                        <button
                          className="apm-sort-btn"
                          onClick={() => { setSortField(field); setSortDir(isActive && sortDir === "asc" ? "desc" : "asc"); }}
                          style={{ display: "inline-flex", alignItems: "center", gap: 4, background: "none", border: "none", cursor: "pointer", fontFamily: T.fontBody, fontSize: 10, fontWeight: 700, letterSpacing: "0.04em", color: isActive ? T.dark : T.muted }}
                        >
                          {label}
                          <span style={{ opacity: isActive ? 1 : 0.4 }}>{sortDir === "asc" && isActive ? "↑" : "↓"}</span>
                        </button>
                      </th>
                    );
                  })}
                  {["Status", "Actions"].map((h, i) => (
                    <th key={h} style={{ padding: "12px 20px", textAlign: i === 1 ? "right" : "left", fontFamily: T.fontBody, fontSize: 10, fontWeight: 700, letterSpacing: "0.04em", color: T.muted }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((project) => (
                  <ProjectRow key={project.id} project={project} onUpdate={load} />
                ))}
                {rows.length === 0 && !loading && (
                  <tr>
                    <td colSpan={5} style={{ padding: 48, textAlign: "center" }}>
                      <FolderOpen size={40} color={T.border} style={{ margin: "0 auto 12px" }} />
                      <p style={{ color: T.muted, margin: 0 }}>No projects found</p>
                      <p style={{ color: T.mutedLight, fontSize: 12, margin: "4px 0 0" }}>Try adjusting your search criteria</p>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── Pagination ───────────────────────────────────────────── */}
      <div className="apm-pager">
        <p style={{ fontSize: 13, color: T.muted, margin: 0, fontFamily: T.fontBody }}>
          Showing <b style={{ color: T.dark }}>{startItem}</b>–<b style={{ color: T.dark }}>{endItem}</b> of <b style={{ color: T.dark }}>{total}</b> projects
        </p>
        <div className="apm-pager-pages">
          <button
            className="apm-btn"
            disabled={page <= 1 || loading}
            onClick={() => setPage(page - 1)}
            style={{ padding: "6px 14px", border: `1px solid ${T.border}`, borderRadius: 6, background: T.surface, cursor: page <= 1 ? "not-allowed" : "pointer", fontSize: 13, opacity: page <= 1 ? 0.4 : 1, fontFamily: T.fontBody }}
          >Previous</button>
          {Array.from({ length: Math.min(5, pages) }, (_, i) => {
            const n = pages <= 5 ? i + 1 : page <= 3 ? i + 1 : page >= pages - 2 ? pages - 4 + i : page - 2 + i;
            return (
              <button key={n} onClick={() => setPage(n)} style={{ width: 36, height: 36, borderRadius: 6, border: `1px solid ${n === page ? T.dark : T.border}`, background: n === page ? T.dark : T.surface, color: n === page ? "#fff" : T.dark, fontWeight: n === page ? 700 : 400, cursor: "pointer", fontSize: 13 }}>{n}</button>
            );
          })}
          <button
            className="apm-btn"
            disabled={page >= pages || loading}
            onClick={() => setPage(page + 1)}
            style={{ padding: "6px 14px", border: `1px solid ${T.border}`, borderRadius: 6, background: T.surface, cursor: page >= pages ? "not-allowed" : "pointer", fontSize: 13, opacity: page >= pages ? 0.4 : 1, fontFamily: T.fontBody }}
          >Next</button>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════
   PROJECT ROW  (desktop table)
═══════════════════════════════════════════════════════════════════ */
function ProjectRow({ project, onUpdate }) {
  const [loading, setLoading] = useState(false);
  const handleApprove = async () => { setLoading(true); try { await admin.approve(project.id); onUpdate(); } finally { setLoading(false); } };
  const handleReject  = async () => { setLoading(true); try { await admin.reject(project.id);  onUpdate(); } finally { setLoading(false); } };

  return (
    <tr className="apm-row" style={{ borderBottom: `1px solid ${T.border}`, opacity: loading ? 0.6 : 1 }}>
      <td style={{ padding: "14px 20px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div style={{ width: 36, height: 36, borderRadius: 8, background: T.amberBg, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
            <FolderOpen size={16} color={T.amber} />
          </div>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontWeight: 600, color: T.dark, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 200 }}>{project.name}</div>
            <div style={{ fontSize: 11, color: T.mutedLight, fontFamily: T.fontBody }}>ID: {project.id.slice(0, 8)}…</div>
          </div>
        </div>
      </td>
      <td style={{ padding: "14px 20px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <User size={13} color={T.mutedLight} />
          <span style={{ color: T.dark, fontSize: 13 }}>{project.ownerEmail}</span>
        </div>
      </td>
      <td style={{ padding: "14px 20px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <Calendar size={13} color={T.mutedLight} />
          <div>
            <div style={{ color: T.dark, fontSize: 13 }}>{formatDate(project.createdAt)}</div>
            <div style={{ fontSize: 11, color: T.mutedLight }}>{new Date(project.createdAt).toLocaleDateString()}</div>
          </div>
        </div>
      </td>
      <td style={{ padding: "14px 20px" }}><StatusBadge status={project.status} /></td>
      <td style={{ padding: "14px 20px", textAlign: "right" }}>
        <a
          href={`${process.env.REACT_APP_EDITOR_URL}/editor/${project.id}`}
          target="_blank"
          rel="noreferrer"
          style={{ display: "inline-flex", alignItems: "center", gap: 4, padding: "6px 14px", fontSize: 12, fontWeight: 600, background: T.dark, color: "#fff", borderRadius: 6, textDecoration: "none" }}
        >
          <Eye size={13} /> View
        </a>
      </td>
    </tr>
  );
}

/* ═══════════════════════════════════════════════════════════════════
   PROJECT CARD  (mobile)
═══════════════════════════════════════════════════════════════════ */
function ProjectCard({ project, onUpdate }) {
  const [loading, setLoading] = useState(false);

  return (
    <div className="apm-card" style={{ opacity: loading ? 0.6 : 1 }}>
      {/* Top section */}
      <div style={{ padding: "14px 16px 12px", display: "flex", alignItems: "flex-start", gap: 12 }}>
        {/* Icon */}
        <div style={{ width: 40, height: 40, borderRadius: 10, background: T.amberBg, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
          <FolderOpen size={18} color={T.amber} />
        </div>

        {/* Info */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontFamily: T.fontBody, fontWeight: 700, fontSize: ".9rem", color: T.dark, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {project.name}
          </div>
          <div style={{ fontFamily: T.fontBody, fontSize: ".6rem", color: T.mutedLight, marginTop: 2 }}>
            ID: {project.id.slice(0, 8)}…
          </div>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 8 }}>
            <StatusBadge status={project.status} />
          </div>
        </div>
      </div>

      {/* Meta row */}
      <div style={{ padding: "10px 16px", borderTop: `1px solid ${T.border}`, background: "#fafafa", display: "flex", flexDirection: "column", gap: 6 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
          <User size={13} color={T.mutedLight} />
          <span style={{ fontFamily: T.fontBody, fontSize: ".8rem", color: T.muted, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{project.ownerEmail}</span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
          <Calendar size={13} color={T.mutedLight} />
          <span style={{ fontFamily: T.fontBody, fontSize: ".8rem", color: T.muted }}>
            {formatDate(project.createdAt)} · {new Date(project.createdAt).toLocaleDateString()}
          </span>
        </div>
      </div>

      {/* Action row */}
      <div style={{ padding: "10px 16px", borderTop: `1px solid ${T.border}`, display: "flex", justifyContent: "flex-end" }}>
        <a
          href={`${process.env.REACT_APP_EDITOR_URL}/editor/${project.id}`}
          target="_blank"
          rel="noreferrer"
          style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "8px 18px", fontSize: 13, fontWeight: 600, background: T.dark, color: "#fff", borderRadius: 8, textDecoration: "none" }}
        >
          <Eye size={14} /> View Project
        </a>
      </div>
    </div>
  );
}
